import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik } from "formik";
import { cloneDeep } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";

import { CurrentFinanceYear } from "../../../../config/app-constants";
import { ISelfTaxPayments } from "../../../../config/interfaces";
import {
  SelfTaxPaymentsFormInitValues,
  SelfTaxPaymentsFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import { UtilService } from "../../../../services";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";
import { DateTimePicker, FormField, NumericInputField } from "../../../fields";

type Props = {
  showCanvas: boolean;
  formValues: ISelfTaxPayments | null;
  handleCloseBtnPress: (salaryData: ISelfTaxPayments | null) => void;
};

const TDSSelfTaxPaymentsDetailsForm: React.FC<Props> = (props: Props) => {
  const { showCanvas, handleCloseBtnPress, formValues } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [initFormValues, setInitFormValues] = useState<ISelfTaxPayments>(
    SelfTaxPaymentsFormInitValues
  );

  useEffect(() => {
    if (showCanvas && formValues) {
      setInitFormValues(() => cloneDeep(formValues));
    } else {
      setInitFormValues(() => cloneDeep(SelfTaxPaymentsFormInitValues));
    }
  }, [showCanvas, formValues]);

  return (
    <>
      <Offcanvas show={showCanvas} placement="end" backdrop={true}>
        <Formik
          enableReinitialize={true}
          initialValues={initFormValues}
          validationSchema={SelfTaxPaymentsFormSchema}
          onSubmit={(values) => handleCloseBtnPress(values)}
        >
          {({
            handleSubmit,
            handleBlur,
            setFieldValue,
            submitCount,
            isValid,
            errors,
            values,
            touched
          }) => (
            <>
              <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

              <Offcanvas.Header>
                <Offcanvas.Title>
                  Provide Tax Paid Challan Details
                  <small>
                    Add any Depreciation losses from the previous years to set off againt the income
                    for AY 2023-2024.
                  </small>
                </Offcanvas.Title>
                <Button
                  variant="primary"
                  className="icon-only hide-offcanvas"
                  onClick={() => handleCloseBtnPress(null)}
                >
                  <FASvgIcon icon={faXmark} />
                </Button>
              </Offcanvas.Header>

              <Offcanvas.Body>
                <section className="mb-5">
                  <div className="section-title-wrap">
                    <h5>Choose a method to add Tax Paid Challan details</h5>
                  </div>
                  <FormField label="Amount" isRequired={true} sm={6}>
                    <NumericInputField
                      showLeftIcon={true}
                      name="Amount"
                      value={values.Amount}
                      onChange={(value) => setFieldValue("Amount", Number(value))}
                      onBlur={handleBlur("Amount")}
                      isInvalid={Boolean(errors.Amount) && touched.Amount}
                      errorMsg={errors.Amount}
                      decimalUpto={0}
                    />
                  </FormField>
                  <FormField label="Date" isRequired={true} sm={6}>
                    <DateTimePicker
                      name="DepositDate"
                      placeholder="DD/MM/YYYY"
                      value={values.DepositDate ? moment(values.DepositDate).toDate() : ""}
                      onChange={(date) => {
                        if (date) {
                          const _date = UtilService.convertToStandardDateFormat(date);
                          setFieldValue("DepositDate", _date);
                        } else {
                          setFieldValue("DepositDate", "");
                        }
                      }}
                      onBlur={handleBlur("DepositDate")}
                      minDate={undefined}
                      maxDate={moment(CurrentFinanceYear).toDate()}
                      isInvalid={Boolean(errors.DepositDate) && touched.DepositDate}
                      errorMsg={errors.DepositDate}
                      isClearable={true}
                    />
                  </FormField>
                  <FormField label="BSR Code" isRequired={true} sm={6}>
                    <NumericInputField
                      name="BSRCode"
                      value={values.BSRCode}
                      onChange={(value) => setFieldValue("BSRCode", Number(value))}
                      onBlur={handleBlur("BSRCode")}
                      isInvalid={Boolean(errors.BSRCode) && touched.BSRCode}
                      errorMsg={errors.BSRCode}
                      decimalUpto={0}
                      separator=""
                    />
                  </FormField>
                  <FormField label="Challan Serial Number" isRequired={true} sm={6}>
                    <NumericInputField
                      name="ChallanNo"
                      value={values.ChallanNo}
                      onChange={(value) => setFieldValue("ChallanNo", Number(value))}
                      onBlur={handleBlur("ChallanNo")}
                      isInvalid={Boolean(errors.ChallanNo) && touched.ChallanNo}
                      errorMsg={errors.ChallanNo}
                      decimalUpto={0}
                      separator=""
                    />
                  </FormField>
                </section>
              </Offcanvas.Body>

              <div className="offcanvas-btns">
                {!isFilingReadOnly ? (
                  <Button variant="outline-white" size="sm" onClick={() => handleSubmit()}>
                    Save and Close
                  </Button>
                ) : (
                  <Button
                    variant="outline-white"
                    size="sm"
                    onClick={() => handleCloseBtnPress(null)}
                  >
                    Close
                  </Button>
                )}
              </div>
            </>
          )}
        </Formik>
      </Offcanvas>
    </>
  );
};

export default TDSSelfTaxPaymentsDetailsForm;
